import { Hash } from "lucide-react";
import { QueryStatus, useQuery } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import { DiscussionTopicsData } from "@/schema/brand-os/ai-consumer-research";

function DiscussionComponent({ status, data }: { status: QueryStatus; data?: DiscussionTopicsData }) {
  switch (status) {
    case "pending":
      return <DiscussionSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data?.channels.length ? <DiscussionContent data={data} /> : <LockedContent message={message} />;
  }
}

export function Discussion() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.DiscussionTopics());

  return (
    <Card className="rounded-2xl bg-card shadow-none w-full gap-3">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="flex size-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-950/50">
            <Hash className="size-5 text-blue-600 dark:text-blue-400" />
          </div>
          <CardTitle className="text-base font-semibold">What People Discuss Per Channel</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <DiscussionComponent status={status} data={data} />
      </CardContent>
    </Card>
  );
}

function DiscussionContent({ data }: { data: DiscussionTopicsData }) {
  return (
    <div className="lg:max-h-[26rem] overflow-y-auto scrollbar-hidden pr-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.channels.map(({ channel, topics }) => (
          <div key={channel} className="rounded-xl border p-4 flex flex-col gap-3 bg-gray-50 min-h-[8.5rem]">
            <h4 className="capitalize text-sm font-semibold text-foreground">{channel}</h4>
            <ul className="space-y-2">
              {topics.map((t, idx) => {
                const isPrimary = idx === 0;
                return (
                  <li key={t.topic} className="flex items-center gap-2 text-sm">
                    <div className={cn("size-1.5 shrink-0 rounded-full", isPrimary ? "bg-destructive" : "bg-muted-foreground/60")} />
                    <span className={cn("capitalize", isPrimary ? "font-semibold text-foreground" : "text-muted-foreground")}>{t.topic}</span>
                    <span className="text-orange-500 ml-auto">{new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(t.percentage)}%</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscussionSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-32 w-full rounded-xl" />
      ))}
    </div>
  );
}
